/**
 * Conversation Service for handling communication with the conversation API
 * Provides methods for retrieving conversation history and analytics
 */
import api from './api';

const conversationService = {
  /**
   * Get conversation history with pagination
   * @param {number} page - Page number (1-based)
   * @param {number} page_size - Number of conversations per page
   * @returns {Promise} - Promise containing conversation history
   */
  getConversationHistory: async (page = 1, page_size = 10) => {
    try {
      console.log('Fetching conversation history:', { page, page_size });
      
      const response = await api.get(`/conversations/history?page=${page}&page_size=${page_size}`);
      
      console.log('Conversation history response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      throw error;
    }
  },

  /**
   * Get a specific conversation by ID
   * @param {string} conversationId - The conversation ID
   * @returns {Promise} - Promise containing conversation details
   */
  getConversationById: async (conversationId) => {
    try {
      console.log('Fetching conversation by ID:', conversationId);
      
      const response = await api.get(`/conversations/${conversationId}`);
      
      console.log('Conversation details response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      throw error;
    }
  },

  /**
   * Delete a conversation
   * @param {string} conversationId - The conversation ID to delete
   * @returns {Promise} - Promise containing deletion result
   */
  deleteConversation: async (conversationId) => {
    try {
      console.log('Deleting conversation:', conversationId);
      
      const response = await api.delete(`/conversations/${conversationId}`);
      
      console.log('Delete conversation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  },

  /**
   * Get conversation analytics
   * @returns {Promise} - Promise containing analytics data
   */
  getConversationAnalytics: async () => {
    try {
      console.log('Fetching conversation analytics');
      
      const response = await api.get('/conversations/analytics');
      
      console.log('Conversation analytics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversation analytics:', error);
      throw error;
    }
  },

  /**
   * Update conversation status
   * @param {number} conversationId - The conversation ID
   * @param {string} status - New status ('active', 'pending', 'resolved')
   * @param {string} notes - Optional notes about the status change
   * @returns {Promise} - Promise containing update result
   */
  updateConversationStatus: async (conversationId, status, notes = null) => {
    try {
      console.log('Updating conversation status:', { conversationId, status, notes });
      
      const response = await api.post(`/conversations/${conversationId}/status`, {
        status,
        notes
      });
      
      console.log('Status update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating conversation status:', error);
      throw error;
    }
  },

  /**
   * Bulk update conversation statuses
   * @param {number[]} conversationIds - Array of conversation IDs
   * @param {string} status - New status ('active', 'pending', 'resolved')
   * @param {string} notes - Optional notes about the status change
   * @returns {Promise} - Promise containing bulk update result
   */
  bulkUpdateConversationStatus: async (conversationIds, status, notes = null) => {
    try {
      console.log('Bulk updating conversation statuses:', { conversationIds, status, notes });
      
      const response = await api.post('/conversations/bulk-update-status', {
        conversation_ids: conversationIds,
        status,
        notes
      });
      
      console.log('Bulk status update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating conversation statuses:', error);
      throw error;
    }
  },

  /**
   * Get conversations filtered by status
   * @param {string} status - Status to filter by ('active', 'pending', 'resolved')
   * @param {number} page - Page number (1-based)
   * @param {number} page_size - Number of conversations per page
   * @returns {Promise} - Promise containing filtered conversations
   */
  getConversationsByStatus: async (status, page = 1, page_size = 10) => {
    try {
      console.log('Fetching conversations by status:', { status, page, page_size });
      
      const response = await api.get(`/conversations/by-status?status=${status}&page=${page}&page_size=${page_size}`);
      
      console.log('Conversations by status response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations by status:', error);
      throw error;
    }
  }
};

export default conversationService;

